/**
 * Kucuk, paylasilan arayuz parcalari.
 *
 * Renk anlamlari token'lardan gelir (yesil = AL/pozitif, kirmizi = SAT/
 * negatif); bilesenlerde ham renk kullanilmaz.
 */

import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";

export type Tone = "up" | "down" | "warn" | "info" | "neutral";

export function Pill({
  tone = "neutral",
  title,
  children,
}: {
  tone?: Tone;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span className="pill" data-tone={tone} title={title}>
      {children}
    </span>
  );
}

/** AL / SAT rozeti - sinyal yonu. */
export function SideBadge({ side, label }: { side: string; label: string }) {
  return (
    <span className="signal-side" data-side={side}>
      {label}
    </span>
  );
}

/**
 * Kalite rozeti - sayi + oranli dolgu cubugu.
 *
 * Skor onaylanmis sinyali ENGELLEMEZ; yalnizca cevresel kosullari ozetler.
 * Bu yuzden skor yoksa (hesaplanamadiysa) satir yine de gosterilir. Renk
 * esikleri arastirmayla dogrulanmis "Yuksek Kalite" (>=5/6) sinirini
 * yansitir (bkz. strategy/quality.py, is_high_quality).
 */
export function Stars({ score, max = 6 }: { score: number | null; max?: number }) {
  if (score == null) {
    return (
      <span className="quality-badge muted" aria-label="Kalite hesaplanamadı">
        —
      </span>
    );
  }
  const ratio = max > 0 ? score / max : 0;
  const tone = ratio >= 5 / 6 ? "up" : ratio >= 0.5 ? "warn" : "neutral";
  const tierLabel = ratio >= 5 / 6 ? "Yüksek" : ratio >= 4 / 6 ? "İyi" : ratio >= 0.5 ? "Orta" : "Düşük";
  return (
    <span
      className="quality-badge"
      data-tone={tone}
      aria-label={`Kalite ${score}/${max} — ${tierLabel}`}
      title={`Kalite ${score}/${max} — ${tierLabel}`}
    >
      <span className="quality-badge-num">{score}</span>
      <span className="quality-badge-text">
        <span className="quality-badge-tier">{tierLabel}</span>
        <span className="quality-badge-bar">
          <span
            className="quality-badge-fill"
            style={{ width: `${Math.max(0, Math.min(1, ratio)) * 100}%` }}
          />
        </span>
      </span>
    </span>
  );
}

/**
 * Mini fiyat grafigi (son 24 kapanmis bar).
 *
 * YALNIZCA backend'in gonderdigi GERCEK kapanislari cizer. Yeterli nokta
 * yoksa hicbir sey cizilmez - uydurma/ornek grafik URETILMEZ.
 */
export function Sparkline({ points, tone }: { points: number[]; tone: "up" | "down" }) {
  if (points.length < 2) return <span className="spark-empty">—</span>;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const step = 100 / (points.length - 1);
  const shape = points
    .map((value, index) => {
      const x = (index * step).toFixed(2);
      const y = (29 - ((value - min) / span) * 26).toFixed(2);
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg className="spark" data-tone={tone} viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true">
      <polyline
        points={shape}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/**
 * Binance'in KENDI statik CDN'i - sembolden turetilebilir, anahtar
 * gerektirmez ve yeni listelenen coinleri de kapsar (olculdu: ekranda
 * gorunen 200 coinin 200'u `image/png`, hepsi FARKLI gorsel; sahte
 * sembol 403 doner). Yalnizca SUSLEMEDIR - tarayici tarafinda yuklenir,
 * botun veri yoluna girmez.
 */
const LOGO_BASE = "https://bin.bnbstatic.com/static/assets/logos";

/**
 * Sembol rozeti: gercek coin logosu; gorsel gelmezse (403 / ag hatasi /
 * cevrimdisi) semboldan TURETILEN harf rozetine duser - liste asla
 * bozulmaz ve hicbir veri kaybolmaz.
 */
export function CoinAvatar({ symbol, base }: { symbol: string; base: string }) {
  const [failed, setFailed] = useState(false);

  let hue = 0;
  for (let index = 0; index < symbol.length; index += 1) {
    hue = (hue * 31 + symbol.charCodeAt(index)) % 360;
  }

  return (
    <span
      className="coin-avatar"
      data-logo={failed ? undefined : "true"}
      style={{ "--coin-hue": hue } as CSSProperties}
      aria-hidden="true"
    >
      {failed ? (
        base.slice(0, 2)
      ) : (
        <img
          src={`${LOGO_BASE}/${encodeURIComponent(base.toUpperCase())}.png`}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      )}
    </span>
  );
}

export function Change({ value, digits = 2 }: { value: number | null; digits?: number }) {
  if (value == null || !Number.isFinite(value)) return <span className="muted">—</span>;
  return (
    <span className={value >= 0 ? "up" : "down"}>
      {value >= 0 ? "+" : ""}
      {value.toFixed(digits)}%
    </span>
  );
}

/** Piyasa taramasi ozet karti - tek bakista sayi + baglam. */
export function Kpi({
  label,
  value,
  sub,
  icon,
  tone = "neutral",
  onClick,
  active,
}: {
  label: string;
  value: string;
  sub?: string;
  icon?: ReactNode;
  tone?: Tone;
  /** Verilirse kart TIKLANABILIR olur (ör. filtre olarak kullanmak icin). */
  onClick?: () => void;
  /** Yalnizca `onClick` ile birlikte anlamlidir - secili/aktif filtreyi vurgular. */
  active?: boolean;
}) {
  const body = (
    <>
      {icon ? (
        <span className="kpi-icon" data-tone={tone}>
          {icon}
        </span>
      ) : null}
      <span className="kpi-body">
        <span className="kpi-label">{label}</span>
        <span className="kpi-value">{value}</span>
        {sub ? <span className="kpi-sub">{sub}</span> : null}
      </span>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className="kpi-card kpi-card-clickable"
        data-tone={tone}
        data-active={active ? "true" : "false"}
        onClick={onClick}
        aria-pressed={active}
      >
        {body}
      </button>
    );
  }

  return (
    <div className="kpi-card" data-tone={tone}>
      {body}
    </div>
  );
}

/** Sembol arama kutusu - istemci tarafinda, yuklenmis listeyi filtreler. */
export function SearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}) {
  return (
    <label className="search-field">
      <IconSearch />
      <input
        type="search"
        className="search-input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </label>
  );
}

/** Acilir filtre secicisi - yerel <select> (klavye/mobil erisimi tarayicidan). */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
  icon,
  disabled,
  title,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (next: string) => void;
  icon?: ReactNode;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <label className="filter-select" data-disabled={disabled ? "true" : undefined} title={title}>
      {icon ? <span className="filter-select-icon">{icon}</span> : null}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        disabled={disabled}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="filter-select-chevron">
        <IconChevronDown />
      </span>
    </label>
  );
}

export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="row">
      <span className="row-label">{label}</span>
      <span className="row-value">{children}</span>
    </div>
  );
}

export function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="card">
      {title ? <h3 className="card-title">{title}</h3> : null}
      {children}
    </section>
  );
}

export function Empty({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {detail ? <div className="note">{detail}</div> : null}
    </div>
  );
}

export function Banner({
  tone = "warn",
  children,
}: {
  tone?: "warn" | "error";
  children: ReactNode;
}) {
  return <div className={tone === "error" ? "warn-banner error-banner" : "warn-banner"}>{children}</div>;
}

export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      className="switch"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
    />
  );
}

export function Setting({
  name,
  hint,
  children,
}: {
  name: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="setting">
      <div className="setting-text">
        <div className="setting-name">{name}</div>
        {hint ? <div className="setting-hint">{hint}</div> : null}
      </div>
      {children}
    </div>
  );
}

/* --- ikonlar (satir ici SVG; disaridan varlik yuklenmez) ----------------- */
export function IconScan() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 14l3-3 3 2 4-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconStar({ filled = false }: { filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        d="M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8-4.2-4.1 5.9-.9z"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconSearch() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
    </svg>
  );
}

export function IconTrendUp() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 17l6-6 4 4 8-8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 7h6v6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconTrendDown() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 7l6 6 4-4 8 8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 17h6v-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconSettings() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
    </svg>
  );
}

/** AL islem dugmesi icin - kalin, yuvarlak uclu tek ok (metin YOK, kullanici
 *  istegi: "butonlarda kesinlikle al sat yazmasın"). */
export function IconArrowUpRight() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
      <path d="M7 17L17 7M8 7h9v9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** SAT islem dugmesi icin - IconArrowUpRight ile AYNI kalinlik/stil. */
export function IconArrowDownRight() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
      <path d="M7 7l10 10M16 7v10H6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconBell() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" strokeLinecap="round" />
      <path d="M10.3 21a2 2 0 0 0 3.4 0" strokeLinecap="round" />
    </svg>
  );
}

export function IconUser() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" strokeLinecap="round" />
    </svg>
  );
}

export function IconRefresh({ spinning = false }: { spinning?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      className={spinning ? "spin" : undefined}
    >
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 4v4h-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconMore() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
      <circle cx="12" cy="5" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="12" cy="19" r="1.7" />
    </svg>
  );
}

export function IconClose() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
    </svg>
  );
}

export function IconShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 3l7 3v6c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V6z" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconTheme() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M21 13a8.5 8.5 0 1 1-10-10 7 7 0 0 0 10 10z" strokeLinejoin="round" />
    </svg>
  );
}

export function IconBarChart() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 20V14M9.3 20V9M14.6 20V12M20 20V5" strokeLinecap="round" />
    </svg>
  );
}

/** Telefondaki alt seritte "Menü" sekmesi (tasan sekmeleri acar). */
export function IconMenu() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  );
}

export function IconClock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
    </svg>
  );
}

export function IconChevronDown() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconFilter() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 5h16M7 12h10M10 19h4" strokeLinecap="round" />
    </svg>
  );
}

export function IconBolt() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
      <path d="M13 2L4 14h7l-1 8 9-12h-7z" />
    </svg>
  );
}

export function IconTarget() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

export function IconDatabase() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </svg>
  );
}

export function IconFlame() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
      <path d="M12 2c1 4 5 5.5 5 11a5 5 0 0 1-10 0c0-2.5 1.3-4 2.5-5 .2 2 1.2 3 2.2 3.2C11 8.5 11 5 12 2z" />
    </svg>
  );
}

/**
 * Kalite halkasi (tablo kolonu) - skor 0..max, halka doluluk orani = skor/max.
 * Etiket esikleri arastirmayla dogrulanmis "Yuksek Kalite" (>=5/6) sinirini
 * yansitir (bkz. strategy/quality.py, is_high_quality). Skor ICAT EDILMEZ:
 * halkanin ortasinda gercek skor yazar (ör. 4/6), 0-100'e olceklenmez.
 */
export function QualityRing({ score, max = 6 }: { score: number | null; max?: number }) {
  if (score == null) return <span className="muted">—</span>;
  const ratio = max > 0 ? Math.max(0, Math.min(1, score / max)) : 0;
  const tone = ratio >= 5 / 6 ? "up" : ratio >= 0.5 ? "warn" : "neutral";
  const label = ratio >= 5 / 6 ? "Yüksek" : ratio >= 4 / 6 ? "İyi" : ratio >= 0.5 ? "Orta" : "Düşük";
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  return (
    <span className="quality-ring" data-tone={tone} title={`Kalite ${score}/${max} — ${label}`}>
      <span className="quality-ring-dial">
        <svg viewBox="0 0 36 36" aria-hidden="true">
          <circle className="quality-ring-track" cx="18" cy="18" r={radius} />
          <circle
            className="quality-ring-value"
            cx="18"
            cy="18"
            r={radius}
            strokeDasharray={`${circumference * ratio} ${circumference}`}
          />
        </svg>
        <span className="quality-ring-num">
          {score}
          <small>/{max}</small>
        </span>
      </span>
      <span className="quality-ring-text">
        <span className="quality-ring-label">{label}</span>
        <span className="quality-ring-bar">
          <span style={{ width: `${ratio * 100}%` }} />
        </span>
      </span>
    </span>
  );
}
